import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {minutesFromDayStart, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {layoutOverlaps, PositionedItem} from "@/agenda/lib/dayLayout";

const MINUTES_PER_DAY = 24 * 60;

/**
 * A vertical column of the hour grid: one day (day and week views) or one
 * collaborator (team view).
 */
export interface AgendaLane {
    key: string | number;
    /** Column header; omitted by the single-column day view. */
    label?: string;
    sublabel?: string;
    /** Calendar day the column represents, driving its slots and now line. */
    day: Date;
    /**
     * Access point of the collaborator the column belongs to, so a slot
     * clicked in it pre-assigns them. Undefined for the day and week columns.
     */
    operatorAccessId?: number | null;
    /**
     * No appointment can ever be created in this column — an operator who has
     * left the pharmacy, whom the form cannot resolve. Clicking a slot there
     * would silently book onto the leaver.
     */
    readonly?: boolean;
    appointments: AgendaAppointment[];
}

export interface PositionedLane extends AgendaLane {
    entries: PositionedItem<AgendaAppointment>[];
}

/**
 * Place each lane's appointments as minute intervals relative to that lane's
 * own day, clamped to it: the endpoint returns boundary-straddling
 * appointments, and in the week view every lane is a different day.
 */
export const positionLanes = (lanes: AgendaLane[]): PositionedLane[] => {
    return lanes.map((lane) => {
        const dayStart = parseAgendaDate(lane.day).startOf('day');

        const timed = lane.appointments
            .map((appointment) => ({
                item: appointment,
                start: Math.max(0, minutesFromDayStart(parseAgendaDate(appointment.start_at), dayStart)),
                end: Math.min(MINUTES_PER_DAY, minutesFromDayStart(parseAgendaDate(appointment.end_at), dayStart))
            }))
            .filter((entry) => entry.end > entry.start);

        return {...lane, entries: layoutOverlaps(timed)};
    });
};
